import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { selectAuthToken } from 'src/store/selectors';
import {
  getCohorts,
  getCohortsbyDataSourceId,
  getCohortById,
  updateCohort,
  cohortOrganizationUnitSync,
} from './cohorts';
import { configuration } from 'src/configuration';
import { Cohort, CohortByIdQueryDTO, CohortsQueryDTO } from 'src/types/cohorts';
import { showToast } from 'src/store/toast';

export function useGetCohortsByDataSourceId(dataSourceId: string | undefined) {
  const token = useSelector(selectAuthToken);

  return useQuery<CohortsQueryDTO | null>({
    queryKey: ['data-source-cohorts', dataSourceId],
    queryFn: async () => {
      if (dataSourceId) {
        return await getCohortsbyDataSourceId({ token, dataSourceId });
      }
      return null;
    },
    staleTime: configuration.queriesStaleTime,
  });
}

export function useGetCohorts() {
  const token = useSelector(selectAuthToken);

  return useQuery<CohortsQueryDTO | null>({
    queryKey: ['data-source-cohorts'],
    queryFn: async () => await getCohorts({ token }),
    refetchOnMount: true,
  });
}

export function useGetCohortById(dataSourceId: string | undefined, cohortId: string | undefined) {
  const token = useSelector(selectAuthToken);

  return useQuery<CohortByIdQueryDTO | null>({
    queryKey: ['cohort', dataSourceId, cohortId],
    queryFn: async () => {
      if (dataSourceId && cohortId) {
        return await getCohortById({ token, dataSourceId, cohortId });
      }
      return null;
    },
    refetchOnMount: true,
    staleTime: configuration.queriesStaleTime,
  });
}

export function useUpdateCohortMutation() {
  const token = useSelector(selectAuthToken);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updatedCohort: Partial<Cohort>) => {
      return updateCohort({ token, cohortToUpdate: updatedCohort });
    },
    onSuccess: (cohortUpdated, variables) => {
      dispatch(
        showToast({
          type: 'success',
          message: 'Cohort updated successfully',
        }),
      );
      // Invalidate all cohort list queries (invalidateQueries automatically refetches active queries)
      queryClient.invalidateQueries({ queryKey: ['data-source-cohorts'] });
      if (variables.dataSourceId) {
        queryClient.invalidateQueries({
          queryKey: ['data-source-cohorts', variables.dataSourceId],
        });
      }
      // Invalidate the specific cohort query
      if (cohortUpdated?.dataSourceId && cohortUpdated?.cohortId) {
        queryClient.invalidateQueries({
          queryKey: ['cohort', cohortUpdated.dataSourceId, cohortUpdated.cohortId],
        });
      }
    },
  });
}

export function useCohortOrganizationUnitSyncMutation() {
  const token = useSelector(selectAuthToken);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updatedCohort: Partial<Cohort> & { organizationUnitIds: string[] }) => {
      return cohortOrganizationUnitSync({
        token,
        dataSourceId: updatedCohort?.dataSourceId,
        cohortId: updatedCohort?.cohortId,
        organizationUnitIds: updatedCohort.organizationUnitIds,
      });
    },
    onSuccess: (cohortUpdated) => {
      dispatch(
        showToast({
          type: 'success',
          message: 'Cohort updated successfully',
        }),
      );
      queryClient.invalidateQueries({ queryKey: ['cohort-organization-units'] });
      queryClient.invalidateQueries({
        queryKey: ['cohort', cohortUpdated.dataSourceId, cohortUpdated.cohortId],
      });
    },
  });
}
